import { clampTo, extractImageFrames } from '../../tools';
import type { atlascloudModelConfig } from '../types';

export const blackForestLabsConfigs: Record<string, atlascloudModelConfig> = {
    'flux-3': {
        // https://www.atlascloud.ai/models/black-forest-labs/flux-3/text-to-video
        // https://www.atlascloud.ai/models/black-forest-labs/flux-3/image-to-video
        // https://www.atlascloud.ai/models/black-forest-labs/flux-3/first-last-frame-to-video
        apiPath: '/api/v1/model/generateVideo',
        pollApiPath: '/api/v1/model/prediction/${requestId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    model: 'black-forest-labs/flux-3/image-to-video',
                    prompt: prompt,
                    aspect_ratio: aspectRatio === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['auto', '21:9', '2:1', '16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20] as const, 5),
                    resolution: clampTo(quality || '720p', ['720p', '1080p'] as const, '720p'),
                    seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                    // 首帧 URL
                    image_url: firstImage,
                    // 可选参数
                    generate_audio: typeof (providerOptions as any)?.audio === 'boolean' ? (providerOptions as any).audio : true,
                    safety_tolerance: typeof (providerOptions as any)?.safety_tolerance === 'number' ? (providerOptions as any).safety_tolerance : 4,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    model: 'black-forest-labs/flux-3/first-last-frame-to-video',
                    prompt: prompt,
                    aspect_ratio: aspectRatio === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['auto', '21:9', '2:1', '16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20] as const, 5),
                    resolution: clampTo(quality || '720p', ['720p', '1080p'] as const, '720p'),
                    seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                    // 首帧 URL
                    start_image_url: firstImage,
                    // 尾帧 URL
                    end_image_url: lastImage,
                    // 可选参数
                    generate_audio: typeof (providerOptions as any)?.audio === 'boolean' ? (providerOptions as any).audio : true,
                    safety_tolerance: typeof (providerOptions as any)?.safety_tolerance === 'number' ? (providerOptions as any).safety_tolerance : 4,
                };
            // 文生视频模式
            } else {
                return {
                    model: 'black-forest-labs/flux-3/text-to-video',
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['auto', '21:9', '2:1', '16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20] as const, 5),
                    resolution: clampTo(quality || '720p', ['720p', '1080p'] as const, '720p'),
                    seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                    // 可选参数
                    generate_audio: typeof (providerOptions as any)?.audio === 'boolean' ? (providerOptions as any).audio : true,
                    safety_tolerance: typeof (providerOptions as any)?.safety_tolerance === 'number' ? (providerOptions as any).safety_tolerance : 4,
                };
            }
        },
    },
    'flux-3-keyframes': {
        // https://www.atlascloud.ai/models/black-forest-labs/flux-3/keyframes-to-video
        apiPath: '/api/v1/model/generateVideo',
        pollApiPath: '/api/v1/model/prediction/${requestId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, aspectRatio, duration, quality, providerOptions } = params;
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 10);
            const maxFrame = Math.max(0, clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20] as const, 5) * 24 - 1);
            const customIndexes = Array.isArray((providerOptions as any)?.frame_indexes)
                ? ((providerOptions as any).frame_indexes as number[])
                    .filter((n) => typeof n === 'number' && Number.isFinite(n))
                    .map((n) => Math.min(maxFrame, Math.max(0, Math.round(n))))
                : undefined;
            const useCustom = !!customIndexes && customIndexes.length === images.length;
            return {
                model: 'black-forest-labs/flux-3/keyframes-to-video',
                prompt: prompt,
                aspect_ratio: aspectRatio === 'match' ? 'auto' : clampTo(aspectRatio || '16:9', ['auto', '21:9', '2:1', '16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20] as const, 5),
                resolution: clampTo(quality || '720p', ['720p', '1080p'] as const, '720p'),
                seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                // 关键帧
                keyframes: images.map((image_url, index) => ({
                    image_url,
                    frame_index: useCustom ? customIndexes[index] : index,
                })),
                // 可选参数
                generate_audio: typeof (providerOptions as any)?.audio === 'boolean' ? (providerOptions as any).audio : true,
                safety_tolerance: typeof (providerOptions as any)?.safety_tolerance === 'number' ? (providerOptions as any).safety_tolerance : 4,
            };
        },
    },
};